import type { PendingDriverSeat } from 'store/trucksSlice'

/** The option value standing for the driver being created — never written into `drivers`, which takes ids. */
export const PENDING_DRIVER_OPTION = '__pending_driver__'

type Slot = 'main' | 'second'

/**
 * The Main and Second Driver pickers over a vehicle being added from Add Driver.
 *
 * The driver being created has no id, so they cannot sit in `drivers`; their seat is held apart, and
 * `createDriver` writes it once they exist (`truckSeat: 'main'` puts them first). `drivers` then carries only
 * the real drivers, in seat order, so the one left over lands in the seat the new driver does not take.
 */
export const pendingDriverSlots = (drivers: string[], seat: PendingDriverSeat): Record<Slot, string> => {
    if (seat === 'main') return { main: PENDING_DRIVER_OPTION, second: drivers[0] ?? '' }
    if (seat === 'second') return { main: drivers[0] ?? '', second: PENDING_DRIVER_OPTION }
    return { main: drivers[0] ?? '', second: drivers[1] ?? '' }
}

/** Pick `value` for one seat. Moving the new driver vacates the seat they left; a real driver is never doubled. */
export const choosePendingDriverSlot = (
    drivers: string[],
    seat: PendingDriverSeat,
    slot: Slot,
    value: string
): { drivers: string[]; seat: PendingDriverSeat } => {
    const slots = pendingDriverSlots(drivers, seat)
    const other: Slot = slot === 'main' ? 'second' : 'main'
    const next: Record<Slot, string> = { ...slots, [slot]: value }
    if (value === PENDING_DRIVER_OPTION && slots[other] === PENDING_DRIVER_OPTION) next[other] = ''
    if (value && value !== PENDING_DRIVER_OPTION && next[other] === value) next[other] = ''
    // A lone driver is Main: `drivers` is a list with no empty first seat, so a new driver left as Second with
    // nobody in Main is seated first when they are created. Show that here rather than a seat they will not get.
    if (next.second === PENDING_DRIVER_OPTION && !next.main) {
        next.main = PENDING_DRIVER_OPTION
        next.second = ''
    }
    const nextSeat: PendingDriverSeat = next.main === PENDING_DRIVER_OPTION ? 'main' : next.second === PENDING_DRIVER_OPTION ? 'second' : null
    return { drivers: [next.main, next.second].filter((id) => id && id !== PENDING_DRIVER_OPTION), seat: nextSeat }
}
